/**
 * 文本 envelope 解析 + 会话预览文本生成（任务 8b-1）。
 *
 * 口径对齐后端 `wsv2/common.go`：
 * - envelope：新字段 c/t/g 优先、旧字段 content/mentionType/mentionTargets 兜底
 *   （与 Go TypeTextMessage.UnmarshalJSON 逐条一致：c 非空才用、t 非 0 才用、
 *   g 非空才用）；非 JSON / 解出 content 为空 → 整串当裸文本（历史数据兼容）。
 * - 预览：PresetMsg 同款映射（clock>0 → 「[阅后即焚消息]」、文本截 25 个码点、
 *   2~8 固定占位）。**刻意偏差**：贴纸 44 后端返回空串（PresetMsg default 分支，
 *   content_list 的 LastMsg 对贴纸就是空），本地生成「[贴纸]」更友好——下次对账
 *   会被服务端空串覆盖，UI 对空预览需容忍（8d）。
 * - @标记：FlushGroupLastMsgPendingQueue 口径——t=1 且 g 含我，或 t=2（全员）。
 *
 * 纯模块（零依赖，不 import Worker 代码）：Worker 做会话 bump、主线程任务 9
 * 渲染文本气泡都用这一份，解析口径不分叉。
 */

export interface TextEnvelope {
  readonly content: string;
  /** @类型：0 无、1 指定人、2 全员 */
  readonly mentionType: number;
  readonly mentionTargets: readonly number[];
  /** 阅后即焚秒数（>0 预览固定文案） */
  readonly clock: number;
}

function numField(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function parseTextEnvelope(msg: string): TextEnvelope {
  let raw: unknown = null;
  // 先看首字符再 JSON.parse（同 messageModel 各 parse*Envelope 的既有口径）：
  // 裸文本消息是合法形态（历史数据、41 编辑帧的 Msg），而「靠抛异常走兜底」在
  // 热路径上很贵——**每抛一次约 9µs**（4×CPU 降速下实测），而本函数在
  // buildTimelineItems 里是每行每次重建都要调的。20e 实测：一场 200 条/秒的风暴
  // 下（635 行 × 181 批）光这一处就烧掉 1.1s 主线程、长帧占比 41%。
  // 行为等价：envelope 一律是 JSON 对象，非 `{` 开头的串原来也都落到裸文本分支
  // （数组/数字/null 解出来取不到 c/content，结果同样是整串当文本）。
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return { content: msg, mentionType: 0, mentionTargets: [], clock: 0 };
  }
  const r = raw as Record<string, unknown>;
  const newContent = typeof r['c'] === 'string' ? r['c'] : '';
  const oldContent = typeof r['content'] === 'string' ? r['content'] : '';
  // 后端：c 非空才用新字段；两者皆空 → 整串当裸文本
  const content = newContent !== '' ? newContent : oldContent;
  const newType = numField(r['t']);
  const newTargets = Array.isArray(r['g']) ? r['g'].filter((x): x is number => typeof x === 'number') : [];
  const oldTargets = Array.isArray(r['mentionTargets'])
    ? r['mentionTargets'].filter((x): x is number => typeof x === 'number')
    : [];
  return {
    content: content !== '' ? content : msg,
    mentionType: newType !== 0 ? newType : numField(r['mentionType']),
    mentionTargets: newTargets.length > 0 ? newTargets : oldTargets,
    clock: numField(r['clock']),
  };
}

/**
 * 发送用文本 envelope（任务 10d）：新字段形态 `{"c":文本,"t":类型,"g":[目标],"clock":0}`，
 * 与旧版发送口径一致（parseTextEnvelope 可原样解回）。@提及（14.11）：t=0 无 /
 * 1 点名（g=目标 uid）/ 2 全员（**g 恒空数组**，旧版 _sendText 同口径）；
 * mention 由 mentionModel.mentionEnvelopeFields 构造。阅后即焚（14.13a）：
 * clock=会话 afterViewTime 秒数，**字段恒写**（旧版 _sendText 同口径：
 * `clock: isGroup ? 0 : burnAfterSeconds`），群聊/未开启传 0。
 */
export function buildTextEnvelope(
  text: string,
  mention?: { readonly type: number; readonly targets: readonly number[] },
  clock = 0,
): string {
  return JSON.stringify({
    c: text,
    t: mention?.type ?? 0,
    g: mention && mention.type === 1 ? [...mention.targets] : [],
    clock: clock > 0 ? clock : 0,
  });
}

/**
 * 发送用图片 envelope（任务 12a）：旧版 sendImage 同形 `{"url":相对路径,"w":宽,"h":高}`，
 * w/h 仅 >0 才写（uploadv2 秒传命中 presign 时代的缓存会返回 0×0，protocol §10.4——
 * 此时省略字段，渲染端走未知尺寸占位）。url 调用方须先过 stripMediaDomain
 * （uploadv2 响应本就是相对路径）。messageModel.parseImageEnvelope 可解回。
 * 阅后即焚（14.13a）：clock>0 才写（旧版 `if (burnAfterSeconds > 0) 'clock'` 同
 * 口径——后端 PresetMsgToTime 读 envelope 顶层 clock 计算焚烧时刻）；相册
 * albumId 走 ext（14.1）。
 */
export function buildImageEnvelope(url: string, width: number, height: number, clock = 0): string {
  const m: Record<string, unknown> = { url };
  if (width > 0) m['w'] = width;
  if (height > 0) m['h'] = height;
  if (clock > 0) m['clock'] = clock;
  return JSON.stringify(m);
}

/**
 * 发送用语音 envelope（任务 14.2b）：旧版 sendVoice 同形
 * `{"url":相对路径,"dur_ms":毫秒,"size":字节,"codec":"aac"|"opus"}`。
 * url 调用方须先过 stripMediaDomain；messageModel.parseVoiceEnvelope 可解回。
 * 阅后即焚（14.13a）：clock>0 才写（旧版同口径）。
 */
export function buildVoiceEnvelope(
  url: string,
  durationMs: number,
  size: number,
  codec: string,
  clock = 0,
): string {
  const m: Record<string, unknown> = { url, dur_ms: durationMs, size, codec };
  if (clock > 0) m['clock'] = clock;
  return JSON.stringify(m);
}

/**
 * 发送用文件/视频 envelope（任务 14.3b）：旧版 _sendUploadedFile/VideoMessage 同形
 * `{"url":相对路径,"fileName":原始文件名,"name":同左,"size":字节,"mime":类型}`
 * （fileName/name 双写与旧版一致，视频同形不带 dur_ms——旧版发送即无此字段）。
 * url 调用方须先过 stripMediaDomain；messageModel.parseFile/VideoEnvelope 可解回。
 * 阅后即焚（14.13a）：clock>0 才写（旧版同口径）。
 */
export function buildFileEnvelope(
  url: string,
  fileName: string,
  size: number,
  mime: string,
  clock = 0,
): string {
  const m: Record<string, unknown> = { url, fileName, name: fileName, size, mime };
  if (clock > 0) m['clock'] = clock;
  return JSON.stringify(m);
}

/**
 * 发送用名片 envelope（任务 14.4）：旧版 sendContactCard 同形
 * `{"UserId":uid 字符串,"Name":昵称,"Avatar":相对路径,"Username":展示 ID}`
 * （**UserId 写字符串**与旧版一致——旧客户端 _readInt 两种都解，保持字节级同形；
 * clock>0 才写，旧版同口径）。avatar 调用方须先过 stripMediaDomain；
 * messageModel.parseContactCardEnvelope 可解回。
 */
export function buildContactCardEnvelope(
  userId: number,
  name: string,
  avatar: string,
  username: string,
  clock = 0,
): string {
  const m: Record<string, unknown> = {
    UserId: String(userId),
    Name: name,
    Avatar: avatar,
    Username: username,
  };
  if (clock > 0) m['clock'] = clock;
  return JSON.stringify(m);
}

/** 群消息是否 @ 到我（t=1 指定人含我 / t=2 全员）。自己发的消息由调用方排除。 */
export function mentionsMe(
  env: Pick<TextEnvelope, 'mentionType' | 'mentionTargets'>,
  myUid: number,
): boolean {
  if (env.mentionType === 2) return true;
  return env.mentionType === 1 && env.mentionTargets.includes(myUid);
}

/** 文本预览截断长度（码点数，后端 []rune 同口径，无省略号） */
const TEXT_PREVIEW_MAX = 25;

const FIXED_PREVIEW: Readonly<Record<number, string>> = {
  2: '[图片]',
  3: '[名片]',
  4: '[语音]',
  5: '[定位]',
  6: '[链接]',
  7: '[媒体]',
  8: '[文件]',
  44: '[贴纸]', // 偏差：服务端为空串（见文件头）
};

/**
 * 系统提示条文案单点映射（任务 14.5，MessageList 提示条与会话预览共用）。
 * 对齐旧版 im_msg_parser._systemText 的固定文案：后端 Msg 是写死的中文串
 * （如「主动退群」「欢迎新的群主：xx」），旧版非 JSON 一律忽略走映射表，
 * 新版同口径（文案统一、不受后端措辞变化影响）。28 例外：Ext 带
 * {"joins":[{uid,nickname}]}（buildJoinMembersExt），有名字时点名展示。
 * 未映射的 note 类型返回空串，调用方兜底。
 */
const NOTE_TEXT: Readonly<Record<number, string>> = {
  22: '群内私聊已允许',
  23: '群内私聊已禁止',
  25: '入群申请已通过',
  26: '已新增群管理员',
  27: '已移除群管理员',
  28: '新成员加入群聊',
  29: '成员已被移出群聊',
  30: '成员已退出群聊',
  31: '群主已转让',
  32: '群资料已更新',
};

/**
 * 仅群主/管理员可见的提示条类型（21ah 人工指图）：30「成员已退出群聊」属管理
 * 信息，刷在全员时间线上只是噪声——普通成员整条不渲染（buildTimelineItems）。
 * ⚠️ 只管本端渲染：帧照收（服务端广播给全员，客户端拦不住也不该拦），会话
 * 列表那一瞬的预览文案也仍可能是它（本地 bump 不知道我在该群的角色，下次
 * 对账/下一条真实消息自然回落）。
 */
export const ADMIN_ONLY_NOTE_TYPES: ReadonlySet<number> = new Set([30]);

/** myRole：0 成员 / 1 管理员 / 2 群主（groupRestrictionModel.groupRoleOf 同口径） */
export function isAdminOnlyNote(msgType: number, myRole: number): boolean {
  return myRole === 0 && ADMIN_ONLY_NOTE_TYPES.has(msgType);
}

const JOIN_NAMES_MAX = 3;

function joinNamesFromExt(ext: string): string[] {
  let raw: unknown;
  try {
    raw = JSON.parse(ext);
  } catch {
    return [];
  }
  if (typeof raw !== 'object' || raw === null) return [];
  const joins = (raw as Record<string, unknown>)['joins'];
  if (!Array.isArray(joins)) return [];
  const names: string[] = [];
  for (const j of joins) {
    if (typeof j !== 'object' || j === null) continue;
    const nickname = (j as Record<string, unknown>)['nickname'];
    if (typeof nickname === 'string' && nickname !== '') names.push(nickname);
  }
  return names;
}

export function systemNoteText(msgType: number, ext: string): string {
  if (msgType === 28) {
    const names = joinNamesFromExt(ext);
    if (names.length === 0) return NOTE_TEXT[28]!;
    const shown = names.slice(0, JOIN_NAMES_MAX).join('、');
    return names.length > JOIN_NAMES_MAX ? `${shown} 等 ${names.length} 人加入群聊` : `${shown} 加入群聊`;
  }
  return NOTE_TEXT[msgType] ?? '';
}

/**
 * 会话列表预览文本（对齐服务端 PresetMsg，使 WS 本地 bump 与下次 content_list
 * 对账的 LastMsg 尽量一致不抖动）。未映射类型返回空串（服务端同款）。
 * 系统提示条走 systemNoteText——服务端 last_msg 不含这批类型（14.5 实锤，
 * msgTypes.ts NOTE 注释），本地 bump 展示注记文案、下次对账回落上一条真实
 * 消息的预览属预期行为。
 */
export function messagePreview(msgType: number, msg: string, ext = ''): string {
  if (msgType === 1) {
    const env = parseTextEnvelope(msg);
    if (env.clock > 0) return '[阅后即焚消息]';
    const points = Array.from(env.content);
    return points.length > TEXT_PREVIEW_MAX ? points.slice(0, TEXT_PREVIEW_MAX).join('') : env.content;
  }
  if (NOTE_TEXT[msgType] !== undefined) return systemNoteText(msgType, ext);
  return FIXED_PREVIEW[msgType] ?? '';
}
